import axios, { API } from 'lib/config/axios';
import PATHS from 'lib/navigation/paths';
import { TOUR_TYPES, TOUR_WIDGET_FIELDS } from 'lib/utils/mainWidgetConstants';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setLoading } from 'redux/features/ui-slice';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';
import useErrorsHandler from 'views/rebranded-module/common/hooks/useErrorsHandler';
import { TourWidgetV2 } from 'views/rebranded-module/common/components/Logical/TourWidgetV2';
import { WL_SS_KEY } from 'views/common/components/Logical/MainWidgetWrapper/utils/constants';
import { useContent } from 'views/rebranded-module/common/context/useContent';

const initTourWidgetData = {
  selectedCountry: null,
  selectedCity: null,
  selectedTour: null,
  selectedState: null,
  selectedTourType: { id: null },
  countries: [],
  cities: [],
  tours: [],
  states: [],
};

const TourWidgetV2Wrapper = (props) => {
  const {
    handleTourWidgetSearchClick,
    whiteLabelWidget = false,
    reservationData = null,
  } = props;

  const content = useContent('TourWidgetComponent');
  const dispatch = useDispatch();
  const handleErrorResponse = useErrorsHandler();
  const appNavigator = useAppNavigator();
  const [tourWidgetData, setTourWidgetData] = useState(initTourWidgetData);

  const tourTypesList = useMemo(
    () => [
      { id: null, name: content.allToursText },
      {
        id: TOUR_TYPES.GUIDED,
        name: content.guidedToursText,
      },
      {
        id: TOUR_TYPES.SELF,
        name: content.selfDriveToursText,
      },
    ],
    [],
  );

  const selectedOptions = useMemo(
    () => ({
      country: tourWidgetData.selectedCountry,
      city: tourWidgetData.selectedCity,
      tour_type: tourWidgetData.selectedTourType,
      state: tourWidgetData.selectedState, // Add this line
    }),
    [tourWidgetData],
  );

  const getTourWidgetData = (queryParams) => {
    let params = queryParams;
    if (!params) {
      params = {
        country_id: tourWidgetData.selectedCountry?.id,
        city_id: tourWidgetData.selectedCity?.id,
        tour_id: tourWidgetData.selectedTour?.id,
        tour_type_id: tourWidgetData.selectedTour?.id,
      };
    }
    dispatch(setLoading(true));
    return axios
      .get(API.tours.search_widget, {
        params: {
          ...params,
          referral_code: reservationData?.referral_code || null,
        },
      })
      .then((res) => {
        setTourWidgetData((prevData) => ({
          ...prevData,
          ...res.data,
        }));
      })
      .catch((error) => {
        handleErrorResponse(error);
      })
      .finally(() => dispatch(setLoading(false)));
  };

  const adjustTourWidgetData = (fieldsToChange) => {
    if (Object.keys(fieldsToChange).length > 0) {
      setTourWidgetData((prevData) => ({
        ...prevData,
        ...fieldsToChange,
      }));
    }
  };

  const getWidgetParams = (fieldType, newValue) => {
    let fieldsToChange = {};
    switch (fieldType) {
      case TOUR_WIDGET_FIELDS.TOUR_TYPE:
        fieldsToChange = {
          selectedTourType: newValue,
          selectedCountry: null,
          selectedCity: null,
          selectedTour: null,
        };
        adjustTourWidgetData(fieldsToChange);
        return {
          tour_type_id: newValue != null ? newValue.id : '',
          country_id:
            fieldsToChange.selectedCountry === null
              ? null
              : tourWidgetData.selectedCountry?.id,
          city_id:
            fieldsToChange.selectedCity === null
              ? null
              : tourWidgetData.selectedCity?.id,
          tour_id:
            fieldsToChange.selectedTour === null
              ? null
              : tourWidgetData.selectedTour?.id,
        };
      case TOUR_WIDGET_FIELDS.STATE: // Add this case
        fieldsToChange.selectedState = newValue;
        if (tourWidgetData.selectedTour) {
          if (
            newValue
            && tourWidgetData.selectedTour.state_id !== newValue.id
          ) {
            fieldsToChange.selectedTour = null;
          }
        }
        adjustTourWidgetData(fieldsToChange);
        return {
          country_id: tourWidgetData.selectedCountry?.id,
          tour_type_id: tourWidgetData.selectedTourType?.id,
          state_id: newValue != null ? newValue.id : '',
          tour_id:
            fieldsToChange.selectedTour === null
              ? null
              : tourWidgetData.selectedTour?.id,
        };

      case TOUR_WIDGET_FIELDS.COUNTRY:
        fieldsToChange.selectedCountry = newValue;
        if (tourWidgetData.selectedCity) {
          if (
            newValue
            && tourWidgetData.selectedCity.country_id !== newValue.id
          ) {
            fieldsToChange.selectedCity = null;
          }
        }
        if (tourWidgetData.selectedTour) {
          if (
            newValue
            && tourWidgetData.selectedTour.country_id !== newValue.id
          ) {
            fieldsToChange.selectedTour = null;
          }
        }
        adjustTourWidgetData(fieldsToChange);
        return {
          tour_type_id: tourWidgetData.selectedTourType?.id,
          country_id: newValue != null ? newValue.id : '',
          city_id:
            fieldsToChange.selectedCity === null
              ? null
              : tourWidgetData.selectedCity?.id,
          tour_id:
            fieldsToChange.selectedTour === null
              ? null
              : tourWidgetData.selectedTour?.id,
        };
      case TOUR_WIDGET_FIELDS.CITY:
        fieldsToChange.selectedCity = newValue;
        if (tourWidgetData.selectedTour) {
          if (newValue && tourWidgetData.selectedTour.city_id !== newValue.id) {
            fieldsToChange.selectedTour = null;
          }
        }
        adjustTourWidgetData(fieldsToChange);
        return {
          country_id: tourWidgetData.selectedCountry?.id,
          tour_type_id: tourWidgetData.selectedTourType?.id,
          city_id: newValue != null ? newValue.id : '',
          tour_id:
            fieldsToChange.selectedTour === null
              ? null
              : tourWidgetData.selectedTour?.id,
        };
      case TOUR_WIDGET_FIELDS.TOUR:
        fieldsToChange.selectedTour = newValue;
        adjustTourWidgetData(fieldsToChange);
        return {
          country_id: tourWidgetData.selectedCountry?.id,
          city_id: tourWidgetData.selectedCity?.id,
          tour_type_id: tourWidgetData.selectedTourType?.id,
          tour_id: newValue != null ? newValue.id : '',
        };
      default:
        return {};
    }
  };

  const handleSelectInputChange = async (fieldType, e, newValue) => {
    const params = getWidgetParams(fieldType, newValue);
    await getTourWidgetData(params);
  };

  const getRedirectionPath = () => {
    if (selectedOptions.tour?.id) {
      const tourSlug = tourWidgetData.selectedTour.slug;
      return PATHS.tour.index(tourWidgetData.tour_type?.slug, tourSlug);
    }
    if (selectedOptions.state?.id) {
      const { selectedState } = tourWidgetData;
      if (tourWidgetData.tour_type?.slug) {
        return PATHS.tour.index(
          tourWidgetData.tour_type.slug,
          selectedState.slug,
        );
      }
      return PATHS.filteredMotorcycleTours(selectedState.slug);
    }
    if (selectedOptions.city?.id) {
      const { selectedCity } = tourWidgetData;
      if (tourWidgetData.tour_type?.slug) {
        return PATHS.tour.index(
          tourWidgetData.tour_type.slug,
          selectedCity.slug,
        );
      }
      return PATHS.filteredMotorcycleTours(selectedCity.slug);
    }
    if (selectedOptions.country?.id) {
      const { selectedCountry } = tourWidgetData;
      if (tourWidgetData.tour_type?.slug) {
        return PATHS.tour.index(
          tourWidgetData.tour_type.slug,
          selectedCountry.slug,
        );
      }
      return PATHS.filteredMotorcycleTours(selectedCountry.slug);
    }
    if (tourWidgetData.tour_type?.slug) {
      return PATHS.tour.tourType(tourWidgetData.tour_type.slug);
    }
    return PATHS.motorcycleTours.index({ q: ' ' });
  };

  const getReferralCode = () => appNavigator.query.ReferralCode || sessionStorage.getItem(WL_SS_KEY);

  const handleSearchButtonClick = () => {
    if (typeof handleTourWidgetSearchClick === 'function') {
      handleTourWidgetSearchClick(selectedOptions);
      return;
    }
    const redirectionPath = getRedirectionPath();
    if (whiteLabelWidget) {
      window.open(
        appNavigator.getUrl(
          undefined,
          getReferralCode()
            ? `${redirectionPath.url}?referral_code=${getReferralCode()}`
            : redirectionPath.url,
        ),
        '_blank',
        'noopener,noreferrer',
      );
    } else {
      appNavigator.push(redirectionPath);
    }
  };

  useEffect(() => {
    getTourWidgetData();
  }, []);

  return (
    <TourWidgetV2
      tourTypesList={tourTypesList}
      countriesList={tourWidgetData.countries}
      statesList={tourWidgetData.states} // Add this line
      handleSelectInputChange={handleSelectInputChange}
      selectedOptions={selectedOptions}
      handleSearchButtonClick={handleSearchButtonClick}
      {...props}
    />
  );
};

export { TourWidgetV2Wrapper };
